import React, { useState, useEffect } from 'react';
import PublicNav from '../components/PublicNav';

const DOJO_API = 'https://dojo.tda-intl.org';

function PruefungsAnmeldungPage() {
  const [termine, setTermine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTermin, setSelectedTermin] = useState(null);
  const [formData, setFormData] = useState({
    vorname: '',
    nachname: '',
    email: '',
    telefon: '',
    verein: '',
    aktueller_gurt: '',
    angestrebter_gurt: '',
    bemerkungen: '',
    datenschutz_akzeptiert: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${DOJO_API}/api/public/pruefungen/termine`)
      .then(r => r.json())
      .then(data => {
        if (data.success) setTermine(data.termine || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleTerminSelect = (termin) => {
    setSelectedTermin(termin);
    setSuccess('');
    setError('');
    window.scrollTo({ top: document.getElementById('anmeldung-formular')?.offsetTop - 20, behavior: 'smooth' });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTermin) {
      setError('Bitte wählen Sie zuerst einen Prüfungstermin aus.');
      return;
    }
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${DOJO_API}/api/public/pruefungen/anmeldung`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          termin_id: selectedTermin.termin_id,
          ...formData
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(data.message || 'Anmeldung erfolgreich! Sie erhalten eine Bestätigung per E-Mail.');
        setFormData({
          vorname: '', nachname: '', email: '', telefon: '',
          verein: '', aktueller_gurt: '', angestrebter_gurt: '',
          bemerkungen: '', datenschutz_akzeptiert: false
        });
        setSelectedTermin(null);
      } else {
        setError(data.error || 'Fehler bei der Anmeldung. Bitte versuchen Sie es erneut.');
      }
    } catch (err) {
      setError('Verbindungsfehler. Bitte versuchen Sie es später erneut.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDatum = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const styles = {
    page: { minHeight: '100vh', background: '#0f172a', color: '#e5e5e5', fontFamily: 'Arial, sans-serif' },
    header: {
      background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
      padding: '40px 20px', textAlign: 'center',
      borderBottom: '2px solid #EAB308'
    },
    title: { color: '#FFD700', fontSize: '2rem', fontWeight: '700', margin: '0 0 8px' },
    subtitle: { color: 'rgba(255,255,255,0.7)', fontSize: '1rem', margin: 0 },
    container: { maxWidth: '900px', margin: '0 auto', padding: '40px 20px' },
    sectionTitle: { color: '#EAB308', fontSize: '1.3rem', fontWeight: '600', marginBottom: '20px', borderBottom: '2px solid #EAB308', paddingBottom: '8px' },
    terminCard: {
      background: '#1e293b', border: '1px solid #334155', borderRadius: '10px',
      padding: '20px', marginBottom: '16px', cursor: 'pointer', transition: 'all 0.2s'
    },
    terminCardSelected: {
      background: '#1e293b', border: '2px solid #EAB308', borderRadius: '10px',
      padding: '20px', marginBottom: '16px', cursor: 'pointer', transition: 'all 0.2s'
    },
    badge: {
      display: 'inline-block', padding: '3px 10px', borderRadius: '4px',
      fontSize: '0.8rem', fontWeight: '600', background: '#EAB308', color: '#000'
    },
    formSection: { background: '#1e293b', border: '1px solid #334155', borderRadius: '10px', padding: '30px', marginTop: '30px' },
    label: { display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.9rem', color: '#cbd5e1' },
    input: {
      width: '100%', padding: '10px 12px', border: '1px solid #475569',
      borderRadius: '6px', fontSize: '0.95rem', background: '#0f172a',
      color: '#e5e5e5', boxSizing: 'border-box', outline: 'none'
    },
    grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' },
    submitBtn: {
      padding: '12px 32px', background: '#EAB308', color: '#000',
      border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '700',
      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px'
    },
    successBox: { background: '#14532d', border: '1px solid #16a34a', borderRadius: '8px', padding: '16px', marginTop: '20px', color: '#86efac' },
    errorBox: { background: '#4a1515', border: '1px solid #ef4444', borderRadius: '8px', padding: '16px', marginTop: '20px', color: '#fca5a5' },
  };

  return (
    <div style={styles.page}>
      <PublicNav />
      <div style={styles.header}>
        <h1 style={styles.title}>🥋 Prüfungsanmeldung</h1>
        <p style={styles.subtitle}>Melden Sie sich für öffentliche TDA-Prüfungen an</p>
      </div>

      <div style={styles.container}>
        {/* Termine Liste */}
        <h2 style={styles.sectionTitle}>Verfügbare Prüfungstermine</h2>

        {loading ? (
          <p style={{ color: '#94a3b8', textAlign: 'center', padding: '40px' }}>Lade Prüfungstermine...</p>
        ) : termine.length === 0 ? (
          <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '10px', padding: '40px', textAlign: 'center' }}>
            <p style={{ color: '#94a3b8', margin: 0, fontSize: '1.1rem' }}>Aktuell sind keine öffentlichen Prüfungstermine verfügbar.</p>
          </div>
        ) : (
          termine.map(termin => (
            <div
              key={termin.termin_id}
              style={selectedTermin?.termin_id === termin.termin_id ? styles.terminCardSelected : styles.terminCard}
              onClick={() => handleTerminSelect(termin)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                    <span style={styles.badge}>{termin.stil_name}</span>
                    {selectedTermin?.termin_id === termin.termin_id && (
                      <span style={{ ...styles.badge, background: '#16a34a', color: '#fff' }}>✓ Ausgewählt</span>
                    )}
                  </div>
                  <h3 style={{ margin: '0 0 8px', color: '#EAB308', fontSize: '1.1rem', fontWeight: '700' }}>
                    {formatDatum(termin.pruefungsdatum)}
                  </h3>
                  <div style={{ display: 'flex', gap: '20px', fontSize: '0.9rem', color: '#94a3b8', flexWrap: 'wrap' }}>
                    {termin.pruefungszeit && <span>⏰ {termin.pruefungszeit} Uhr</span>}
                    {termin.pruefungsort && <span>📍 {termin.pruefungsort}</span>}
                    <span>🏛 {termin.dojoname}</span>
                    {termin.pruefungsgebuehr && <span>💶 {termin.pruefungsgebuehr} €</span>}
                  </div>
                  {termin.anmeldefrist && (
                    <p style={{ margin: '8px 0 0', fontSize: '0.85rem', color: '#f59e0b' }}>
                      ⚠️ Anmeldefrist: {formatDatum(termin.anmeldefrist)}
                    </p>
                  )}
                </div>
                <div style={{
                  padding: '8px 16px', background: '#EAB308', color: '#000',
                  borderRadius: '6px', fontWeight: '600', fontSize: '0.9rem',
                  alignSelf: 'center', whiteSpace: 'nowrap'
                }}>
                  Auswählen →
                </div>
              </div>
            </div>
          ))
        )}

        {/* Anmeldeformular */}
        <div id="anmeldung-formular" style={styles.formSection}>
          <h2 style={{ ...styles.sectionTitle, marginTop: 0 }}>
            {selectedTermin
              ? `Anmeldung: ${selectedTermin.stil_name} – ${formatDatum(selectedTermin.pruefungsdatum)}`
              : 'Anmeldeformular'}
          </h2>

          {!selectedTermin && (
            <p style={{ color: '#94a3b8', marginBottom: '20px' }}>
              Bitte wählen Sie oben einen Prüfungstermin aus, um das Formular auszufüllen.
            </p>
          )}

          <form onSubmit={handleSubmit}>
            <div style={styles.grid2}>
              <div>
                <label style={styles.label}>Vorname *</label>
                <input style={styles.input} type="text" name="vorname" value={formData.vorname} onChange={handleChange} required placeholder="Max" />
              </div>
              <div>
                <label style={styles.label}>Nachname *</label>
                <input style={styles.input} type="text" name="nachname" value={formData.nachname} onChange={handleChange} required placeholder="Mustermann" />
              </div>
            </div>

            <div style={styles.grid2}>
              <div>
                <label style={styles.label}>E-Mail *</label>
                <input style={styles.input} type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="max@example.com" />
              </div>
              <div>
                <label style={styles.label}>Telefon *</label>
                <input style={styles.input} type="tel" name="telefon" value={formData.telefon} onChange={handleChange} required placeholder="+49 123 456789" />
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={styles.label}>Verein / Dojo (optional)</label>
              <input style={styles.input} type="text" name="verein" value={formData.verein} onChange={handleChange} placeholder="Name Ihres Vereins oder Dojos" />
            </div>

            <div style={styles.grid2}>
              <div>
                <label style={styles.label}>Aktueller Gurt (optional)</label>
                <input style={styles.input} type="text" name="aktueller_gurt" value={formData.aktueller_gurt} onChange={handleChange} placeholder="z.B. 5. Kyu (gelb-oranger Gurt)" />
              </div>
              <div>
                <label style={styles.label}>Angestrebter Gurt (optional)</label>
                <input style={styles.input} type="text" name="angestrebter_gurt" value={formData.angestrebter_gurt} onChange={handleChange} placeholder="z.B. 4. Kyu (oranger Gurt)" />
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={styles.label}>Bemerkungen (optional)</label>
              <textarea
                style={{ ...styles.input, minHeight: '80px', resize: 'vertical', fontFamily: 'inherit' }}
                name="bemerkungen" value={formData.bemerkungen} onChange={handleChange}
                placeholder="Weitere Informationen, besondere Umstände, etc."
                rows="3"
              />
            </div>

            <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <input
                type="checkbox" id="datenschutz" name="datenschutz_akzeptiert"
                checked={formData.datenschutz_akzeptiert} onChange={handleChange}
                required style={{ marginTop: '3px', accentColor: '#EAB308', width: '16px', height: '16px' }}
              />
              <label htmlFor="datenschutz" style={{ fontSize: '0.9rem', color: '#94a3b8', cursor: 'pointer' }}>
                Ich akzeptiere die{' '}
                <a href="https://www.tda-intl.com/datenschutz" target="_blank" rel="noopener noreferrer" style={{ color: '#EAB308' }}>
                  Datenschutzerklärung
                </a>{' '}
                und stimme der Verarbeitung meiner Daten zur Bearbeitung meiner Anmeldung zu. *
              </label>
            </div>

            <button type="submit" style={styles.submitBtn} disabled={submitting || !selectedTermin}>
              {submitting ? '⏳ Wird gesendet...' : '🥋 Jetzt anmelden'}
            </button>

            <p style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '12px' }}>* Pflichtfelder</p>
          </form>

          {success && (
            <div style={styles.successBox}>
              <strong>✅ Erfolg!</strong><br />{success}
            </div>
          )}
          {error && (
            <div style={styles.errorBox}>
              <strong>❌ Fehler:</strong><br />{error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PruefungsAnmeldungPage;
